import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  GitCompareArrows, TrendingUp, Trash2, AlertTriangle, CheckCircle2,
  Plus, Minus, Settings, Loader2, Brain, Clock, ArrowRight, RefreshCw,
  Filter, ShieldAlert, Zap, X, ChevronDown, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import SubscriptionFilter from "@/components/SubscriptionFilter";

const CHANGE_TYPE_META = {
  config_changed: { label: "Config Changed", icon: Settings, color: "text-amber-600 dark:text-amber-400", accent: "var(--hig-warning)" },
  resource_created: { label: "Created", icon: Plus, color: "text-emerald-600 dark:text-emerald-400", accent: "var(--hig-success)" },
  resource_deleted: { label: "Deleted", icon: Minus, color: "text-red-600 dark:text-red-400", accent: "var(--hig-danger)" },
  orphan_detected: { label: "Orphan Detected", icon: AlertTriangle, color: "text-orange-600 dark:text-orange-400", accent: "var(--hig-warning)" },
  orphan_resolved: { label: "Orphan Resolved", icon: CheckCircle2, color: "text-[var(--hig-accent)]", accent: "var(--hig-accent)" },
  cleanup_executed: { label: "Cleanup", icon: Trash2, color: "text-violet-600 dark:text-violet-400", accent: "#af52de" },
  discovered: { label: "Discovered", icon: Eye, color: "text-[var(--hig-accent)]", accent: "var(--hig-accent)" },
};

const ANOMALY_CAT = {
  security: { icon: ShieldAlert, color: "var(--hig-danger)" },
  bulk_operation: { icon: Zap, color: "var(--hig-warning)" },
  timing: { icon: Clock, color: "var(--hig-warning)" },
  drift: { icon: GitCompareArrows, color: "#af52de" },
  orphan_velocity: { icon: AlertTriangle, color: "var(--hig-warning)" },
  ghost_resource: { icon: Minus, color: "var(--hig-danger)" },
};

const SEV_DOT = { critical: "var(--hig-danger)", high: "var(--hig-warning)", medium: "var(--hig-warning)", low: "var(--hig-accent)" };

function AnomalyCard({ anomaly }) {
  const cat = ANOMALY_CAT[anomaly.category] || ANOMALY_CAT.security;
  const Icon = cat.icon;
  const dot = SEV_DOT[anomaly.severity] || SEV_DOT.medium;

  return (
    <div className="group rounded-xl overflow-hidden hig-card" style={{ background: "var(--n-surface-2)", border: `1px solid color-mix(in srgb, ${cat.color} 15%, transparent)` }} data-testid={`anomaly-${anomaly.id}`}>
      <div className="h-px w-full" style={{ background: `linear-gradient(90deg, ${cat.color}60, transparent)` }} />
      <div className="p-5 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${cat.color}12` }}>
            <Icon className="w-4 h-4" style={{ color: cat.color }} />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
            <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: dot }}>{anomaly.severity}</span>
          </div>
        </div>
        <h3 className="text-sm font-semibold leading-snug">{anomaly.title}</h3>
        <p className="text-xs text-[var(--n-text-secondary)] leading-relaxed">{anomaly.description}</p>
        {anomaly.recommendation && (
          <p className="text-xs text-[var(--hig-accent)]/80 flex items-center gap-1.5"><ArrowRight className="w-3 h-3" /> {anomaly.recommendation}</p>
        )}
      </div>
    </div>
  );
}

function ChangeBadge({ type }) {
  const meta = CHANGE_TYPE_META[type] || { label: type, icon: GitCompareArrows, color: "text-muted-foreground" };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${meta.color}`} style={{ background: `color-mix(in srgb, ${meta.accent} 12%, transparent)`, border: `1px solid color-mix(in srgb, ${meta.accent} 20%, transparent)` }}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function ChangeRow({ change }) {
  const shortType = change.resource_type?.split("/").pop() || "?";
  const changes = change.changes || {};
  return (
    <div className="flex items-start gap-3 px-4 py-3.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] hig-card" data-testid={`change-${change.id}`}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `color-mix(in srgb, ${CHANGE_TYPE_META[change.change_type]?.accent || "var(--n-text-muted)"} 15%, transparent)` }}>
        {(() => {
          const RowIcon = CHANGE_TYPE_META[change.change_type]?.icon;
          return RowIcon ? (
            <RowIcon className="w-3.5 h-3.5" style={{ color: CHANGE_TYPE_META[change.change_type]?.accent }} />
          ) : (
            <GitCompareArrows className="w-3.5 h-3.5 text-[var(--n-text-muted)]" />
          );
        })()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium truncate">{change.resource_name}</span>
          <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)] text-[var(--n-text-muted)]">{shortType}</Badge>
          <ChangeBadge type={change.change_type} />
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-[var(--n-text-muted)]">
          <span>{change.resource_group}</span>
          {change.credential_name && <span>{change.credential_name}</span>}
        </div>
        {Object.keys(changes).length > 0 && change.change_type === "config_changed" && (
          <div className="mt-1.5 space-y-1">
            {Object.entries(changes).slice(0, 3).map(([key, val]) => (
              <div key={key} className="flex items-center gap-2 text-[10px] bg-[var(--n-bg)] rounded px-2 py-0.5 border border-[var(--n-border)]">
                <span className="text-[var(--n-text-muted)] font-mono">{key}:</span>
                <span className="text-red-600 dark:text-red-400/60 line-through truncate max-w-[100px]">{String(val?.old || "")}</span>
                <ArrowRight className="w-2.5 h-2.5 text-[var(--n-text-dim)] shrink-0" />
                <span className="text-emerald-600 dark:text-emerald-400 truncate max-w-[100px]">{String(val?.new || "")}</span>
              </div>
            ))}
          </div>
        )}
        {change.change_type === "orphan_detected" && changes.orphan_reason && (
          <p className="mt-1 text-[10px] text-orange-600 dark:text-orange-400/70">{changes.orphan_reason}</p>
        )}
      </div>
      <span className="text-[10px] text-[var(--n-text-muted)] shrink-0 text-right whitespace-nowrap">
        {new Date(change.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" })}<br />
        {new Date(change.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
      </span>
    </div>
  );
}

function MiniBarChart({ data, maxBars = 14 }) {
  const entries = Object.entries(data).slice(-maxBars);
  if (!entries.length) return null;
  const maxVal = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="flex items-end gap-[3px] h-14">
      {entries.map(([date, count]) => (
        <div key={date} className="flex-1 flex flex-col items-center gap-0.5">
          <div className="w-full bg-[var(--hig-accent)]/30 rounded-sm min-h-[2px] hover:bg-[var(--hig-accent)]/60 transition-colors" style={{ height: `${(count / maxVal) * 100}%` }} title={`${date}: ${count}`} />
        </div>
      ))}
    </div>
  );
}

export default function ChangeManagementPage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [changes, setChanges] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [changeTypeFilter, setChangeTypeFilter] = useState(searchParams.get("type") || "all");
  const [days, setDays] = useState(30);
  const [anomalies, setAnomalies] = useState(null);
  const [anomalyLoading, setAnomalyLoading] = useState(true);
  const [deepAnalysis, setDeepAnalysis] = useState(null);
  const [deepAnalyzing, setDeepAnalyzing] = useState(false);
  const [page, setPage] = useState(0);
  const LIMIT = 30;

  const fetchData = useCallback(async () => {
    setLoading(true);
    const sub = subscriptionId ? `&subscription_id=${subscriptionId}` : "";
    const type = changeTypeFilter !== "all" ? `&change_type=${changeTypeFilter}` : "";
    const [summaryRes, changesRes] = await Promise.allSettled([
      api().get(`/changes/summary?days=${days}${sub}`),
      api().get(`/changes?days=${days}&limit=${LIMIT}&offset=${page * LIMIT}${sub}${type}`),
    ]);
    if (summaryRes.status === "fulfilled") setSummary(summaryRes.value.data);
    if (changesRes.status === "fulfilled") {
      setChanges(changesRes.value.data.changes);
      setTotal(changesRes.value.data.total);
    }
    setLoading(false);
  }, [api, subscriptionId, changeTypeFilter, days, page]);

  const fetchAnomalies = useCallback(async () => {
    setAnomalyLoading(true);
    try {
      const sub = subscriptionId ? `&subscription_id=${subscriptionId}` : "";
      const res = await api().get(`/changes/anomalies?days=${days}${sub}`);
      setAnomalies(res.data);
    } catch { setAnomalies(null); }
    finally { setAnomalyLoading(false); }
  }, [api, subscriptionId, days]);

  useEffect(() => { fetchData(); fetchAnomalies(); }, [fetchData, fetchAnomalies]);

  const runDeepAnalysis = async () => {
    setDeepAnalyzing(true);
    try {
      const sub = subscriptionId ? `&subscription_id=${subscriptionId}` : "";
      const res = await api().post(`/changes/ai-analysis?days=${days}${sub}`);
      setDeepAnalysis(res.data);
      toast.success("Deep analysis complete");
    } catch { toast.error("Analysis failed"); }
    finally { setDeepAnalyzing(false); }
  };

  const riskColor = anomalies?.risk_level === "critical" ? "var(--hig-danger)" : anomalies?.risk_level === "high" ? "var(--hig-warning)" : anomalies?.risk_level === "medium" ? "var(--hig-warning)" : "var(--hig-accent)";

  if (loading && !summary) {
    return (
      <div className="space-y-6" data-testid="changes-page">
        <Skeleton className="h-8 w-48 bg-[var(--n-border-2)]" />
        <div className="grid grid-cols-3 gap-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 bg-[var(--n-border-2)] rounded-xl" />)}</div>
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 bg-[var(--n-border-2)] rounded-lg" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-[1100px] mx-auto" data-testid="changes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Activity</h1>
          <p className="text-xs text-[var(--n-text-muted)] mt-0.5">AI-monitored resource changes, anomalies & audit trail</p>
        </div>
        <div className="flex items-center gap-2">
          <SubscriptionFilter value={subscriptionId} onChange={(v) => { setSubscriptionId(v); setPage(0); }} />
          <Select value={String(days)} onValueChange={(v) => { setDays(Number(v)); setPage(0); }}>
            <SelectTrigger className="w-20 bg-[var(--n-surface)] border-[var(--n-border)] text-xs h-8" data-testid="days-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="7">7d</SelectItem><SelectItem value="14">14d</SelectItem><SelectItem value="30">30d</SelectItem><SelectItem value="90">90d</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      {/* Needs Attention — Auto-AI Anomalies */}
      {anomalyLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-36 bg-[var(--n-surface-2)] rounded-xl" />)}
        </div>
      ) : anomalies?.anomalies?.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5">
              <ShieldAlert className="w-4 h-4" style={{ color: riskColor }} />
              <span className="text-xs font-semibold text-[var(--n-text-secondary)] uppercase tracking-widest">Needs Attention</span>
            </div>
            <div className="flex-1 h-px bg-[var(--n-border)]" />
            <span className="text-[10px] font-mono font-semibold" style={{ color: riskColor }}>{anomalies.risk_level} risk</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="anomaly-grid">
            {anomalies.anomalies.slice(0, 3).map((a, i) => <AnomalyCard key={a.id || i} anomaly={a} />)}
          </div>
          {anomalies.summary && (
            <p className="text-xs text-[var(--n-text-muted)] pl-1">{anomalies.summary}</p>
          )}
        </div>
      )}

      {/* Stats Strip */}
      {summary && (
        <div className="flex flex-wrap gap-x-8 gap-y-3 px-5 py-4 rounded-xl bg-[var(--n-surface)] border border-[var(--n-border)]">
          <div><span className="text-base font-bold font-mono text-[var(--hig-accent)]">{summary.total_changes}</span><span className="text-[10px] text-[var(--n-text-muted)] ml-2 uppercase">Changes</span></div>
          <div><span className="text-base font-bold font-mono">{summary.today}</span><span className="text-[10px] text-[var(--n-text-muted)] ml-2 uppercase">Today</span></div>
          <div><span className="text-base font-bold font-mono">{summary.this_week}</span><span className="text-[10px] text-[var(--n-text-muted)] ml-2 uppercase">This Week</span></div>
          <div><span className="text-base font-bold font-mono text-orange-600 dark:text-orange-400">{summary.orphans?.detected || 0}</span><span className="text-[10px] text-[var(--n-text-muted)] ml-2 uppercase">Orphans</span></div>
          <div><span className="text-base font-bold font-mono text-violet-600 dark:text-violet-400">{summary.cleanups?.total_events || 0}</span><span className="text-[10px] text-[var(--n-text-muted)] ml-2 uppercase">Cleanups</span></div>
          <div className="flex-1 min-w-[120px]"><MiniBarChart data={summary.daily_trend || {}} /></div>
        </div>
      )}

      {/* Type Filters + Deep Analysis */}
      <div className="flex flex-wrap items-center gap-1.5">
        {summary && Object.entries(summary.by_change_type || {}).map(([type, count]) => {
          const meta = CHANGE_TYPE_META[type] || {};
          const active = changeTypeFilter === type;
          return (
            <button
              key={type}
              onClick={() => { setChangeTypeFilter(active ? "all" : type); setPage(0); }}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all ${active ? "text-foreground" : "text-[var(--n-text-muted)] hover:text-[var(--n-text-secondary)]"}`}
              style={active ? { background: `color-mix(in srgb, ${meta.accent} 15%, transparent)`, border: `1px solid color-mix(in srgb, ${meta.accent} 25%, transparent)` } : { background: "transparent", border: "1px solid var(--n-border)" }}
              data-testid={`filter-${type}`}
            >
              <ChangeBadge type={type} /> <span className="font-mono">{count}</span>
            </button>
          );
        })}
        {changeTypeFilter !== "all" && (
          <button onClick={() => { setChangeTypeFilter("all"); setPage(0); }} className="text-[10px] text-[var(--n-text-dim)] hover:text-foreground flex items-center gap-0.5 ml-1"><X className="w-3 h-3" /> Clear</button>
        )}
        <div className="flex-1" />
        <Button onClick={runDeepAnalysis} disabled={deepAnalyzing} variant="ghost" size="sm" className="text-[10px] text-[var(--n-text-muted)] hover:text-[var(--hig-accent)] h-7" data-testid="deep-analysis-btn">
          {deepAnalyzing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Brain className="w-3 h-3 mr-1" />}
          {deepAnalyzing ? "Analyzing..." : "Deep Analysis"}
        </Button>
      </div>

      {/* Deep Analysis Result */}
      {deepAnalysis?.analysis && (
        <Card className="bg-[var(--hig-accent)]/[0.03] border-[var(--hig-accent)]/15">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2"><Brain className="w-3.5 h-3.5 text-[var(--hig-accent)]" /><span className="text-[10px] font-semibold text-[var(--n-text-secondary)] uppercase tracking-wider">Deep Analysis</span></div>
              <button onClick={() => setDeepAnalysis(null)} className="text-[10px] text-[var(--n-text-dim)] hover:text-foreground"><X className="w-3 h-3" /></button>
            </div>
            <div className="text-[11px] text-[var(--n-text-secondary)] leading-relaxed whitespace-pre-wrap">{deepAnalysis.analysis}</div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-[var(--hig-accent)]" />
            <span className="text-[11px] font-semibold text-[var(--n-text-secondary)] uppercase tracking-widest">Timeline</span>
          </div>
          <div className="flex-1 h-px bg-[var(--n-border-2)]" />
          <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)] text-[var(--n-text-muted)]">{total}</Badge>
          <Button variant="ghost" size="icon" onClick={() => { fetchData(); fetchAnomalies(); }} className="h-6 w-6 text-[var(--n-text-dim)] hover:text-[var(--hig-accent)]" data-testid="refresh-changes"><RefreshCw className="w-3 h-3" /></Button>
        </div>

        {changes.length === 0 ? (
          <div className="text-center py-12 bg-[var(--n-surface)] rounded-xl border border-[var(--n-border)]">
            <GitCompareArrows className="w-6 h-6 text-[var(--n-border-3)] mx-auto mb-2" />
            <p className="text-xs text-[var(--n-text-dim)]">No changes for selected filters</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {changes.map((c) => <ChangeRow key={c.id} change={c} />)}
          </div>
        )}

        {total > LIMIT && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} className="border-[var(--n-border)] text-xs h-7" data-testid="prev-page">Previous</Button>
            <span className="text-[10px] text-[var(--n-text-dim)]">Page {page + 1} / {Math.ceil(total / LIMIT)}</span>
            <Button variant="outline" size="sm" disabled={(page + 1) * LIMIT >= total} onClick={() => setPage(p => p + 1)} className="border-[var(--n-border)] text-xs h-7" data-testid="next-page">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}
